import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ibeflix_token";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// FastAPI 422 returns detail as an array of {msg,...} objects; other errors return a
// string. Always coerce to a readable string so users never see "[object Object]".
function formatDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msg = detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
    return msg || fallback;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const wsRef = useRef(null);
  const roomRef = useRef(null);

  const authFetch = useCallback(async (path, opts = {}) => {
    const t = localStorage.getItem(TOKEN_KEY);
    let res;
    try {
      res = await fetch(`${API}${path}`, {
        ...opts,
        headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}), ...(opts.headers || {}) },
      });
    } catch (e) {
      throw new Error("Can't reach the server. Check your internet connection and try again.");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) throw new Error(formatDetail(data.detail, "Incorrect email or password."));
      if (res.status === 409 || res.status === 400 || res.status === 422) throw new Error(formatDetail(data.detail, "Please check your details and try again."));
      if (res.status >= 500) throw new Error(formatDetail(data.detail, "Server error — please try again in a moment."));
      throw new Error(formatDetail(data.detail, "Something went wrong. Please try again."));
    }
    return data;
  }, []);

  const refreshFriends = useCallback(async () => {
    try {
      const d = await authFetch("/friends");
      setFriends(d.friends || []);
      setIncoming(d.incoming || []);
    } catch (e) { /* friends refresh failed */ }
  }, [authFetch]);

  // load account from token
  useEffect(() => {
    let active = true;
    if (!token) { setLoading(false); setAccount(null); return; }
    authFetch("/auth/me")
      .then((d) => { if (active) setAccount(d.user); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); if (active) { setToken(null); setAccount(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token, authFetch]);

  // Emergent Google OAuth callback: exchange session_id (URL fragment) for our app token.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!sid) return;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/google/session`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sid }),
        });
        const d = await res.json().catch(() => ({}));
        if (res.ok && d.token) {
          localStorage.setItem(TOKEN_KEY, d.token);
          setToken(d.token);
          setAccount(d.user);
        }
      } catch (e) { /* google sign-in exchange failed */ }
    })();
  }, []);

  // presence websocket
  useEffect(() => {
    if (!account || !token) return;
    let closed = false;
    let retry;
    const connect = () => {
      const wsUrl = API.replace(/^http/i, "ws") + `/ws/presence?token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        refreshFriends();
        if (roomRef.current) ws.send(JSON.stringify({ type: "enter_room", roomCode: roomRef.current }));
      };
      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "friends_changed") refreshFriends();
        else if (msg.type === "friend_request") { refreshFriends(); toast(`👋 @${msg.from.username} sent you a friend request`); }
        else if (msg.type === "streak_milestone") {
          refreshFriends();
          toast(`🏆 ${msg.milestone} movies with ${msg.friend.avatarEmoji} @${msg.friend.username}! You two are a duo.`, { duration: 8000 });
        }
        else if (msg.type === "party_invite") {
          toast(`🎬 @${msg.from.username} started a watch party!`, {
            duration: 20000,
            action: { label: "Join", onClick: () => navigate(`/room/${msg.roomCode}`) },
          });
        }
      };
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch (e) { console.debug(e); } };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); try { wsRef.current?.close(); } catch (e) { console.debug(e); } };
  }, [account, token, refreshFriends, navigate]);

  const sendPresence = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const enterRoom = useCallback((code) => {
    roomRef.current = code;
    sendPresence({ type: "enter_room", roomCode: code });
  }, [sendPresence]);

  const leaveRoom = useCallback(() => {
    roomRef.current = null;
    sendPresence({ type: "leave_room" });
  }, [sendPresence]);

  const login = useCallback(async (email, password) => {
    const d = await authFetch("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    localStorage.setItem(TOKEN_KEY, d.token);
    setToken(d.token); setAccount(d.user);
    return d.user;
  }, [authFetch]);

  const signup = useCallback(async (payload) => {
    const d = await authFetch("/auth/signup", { method: "POST", body: JSON.stringify(payload) });
    localStorage.setItem(TOKEN_KEY, d.token);
    setToken(d.token); setAccount(d.user);
    return d.user;
  }, [authFetch]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    try { authFetch("/auth/logout", { method: "POST" }); } catch (e) { /* best-effort */ }
    try { wsRef.current?.close(); } catch (e) { console.debug(e); }
    setToken(null); setAccount(null); setFriends([]); setIncoming([]);
  }, [authFetch]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const value = useMemo(
    () => ({ account, loading, friends, incoming, refreshFriends, authFetch, sendPresence, enterRoom, leaveRoom, login, signup, logout, loginWithGoogle }),
    [account, loading, friends, incoming, refreshFriends, authFetch, sendPresence, enterRoom, leaveRoom, login, signup, logout, loginWithGoogle],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
